import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
} from '@angular/core';
import { Operators } from '@rivethealth/data/advanced-filters';
import { Option } from '@rivethealth/data/options';
import {
  Filter,
  friendlyMultiSelectOperator,
  Operator,
} from '@rivethealth/data/super-table';
import { AnalyticsEventOutput } from '../analytics/common-analytics.service';
import { HookDirective } from '../core/hook';
import {
  useEffect,
  useMemo,
  useState,
  useSubscriptionLatest,
} from '../core/hooks';
import { OptionsLoader } from '../input/option-select.component';
import { FiltersComponent } from './filters.component';

@Component({
  selector: 'advanced-filter-single-select',
  templateUrl: './advanced-filter-single-select.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdvancedFilterSingleSelectComponent<
  Name extends string,
> extends HookDirective {
  constructor(
    changeDetector: ChangeDetectorRef,
    private readonly filters: FiltersComponent<{
      [K in Name]: Filter<string[]>;
    }>,
  ) {
    super(changeDetector);
  }

  @Input()
  name: Name;

  @Input('optional')
  optional: boolean = false;

  @Input('options')
  options: Option[] = [];

  @Input()
  optionsLoader: OptionsLoader;

  @Input()
  optionsFilterable: boolean;

  @Input('operators')
  operators: Operator[];

  @Input()
  selectedEnabled: boolean = true;

  @Input()
  locked: boolean = false;

  @Input()
  readonly analyticsEvents: {
    changeFilter?: AnalyticsEventOutput<void>;
  } = {};

  @Input()
  placeholder: string = 'Select...';

  @Input()
  hideOperators: boolean = false;

  get _() {
    const filters = useSubscriptionLatest(this.filters.value, []);
    const thisFilter = useMemo(
      () => filters?.[this.name],
      [filters, this.name],
    );

    const [defaultOperators, setDefaultOperators] = useState<Operator[]>([]);

    useEffect(() => {
      const newDefaultOperators: Operator[] = this.optional
        ? ['ANY_EQ', 'NOT_ANY_EQ', 'EMPTY', 'NOT_EMPTY']
        : ['ANY_EQ', 'NOT_ANY_EQ'];
      setDefaultOperators(newDefaultOperators);
    }, [this.optional]);

    const [operators, setOperators] = useState<Operator[]>([]);
    useEffect(() => {
      setOperators([...defaultOperators, ...(this.operators || [])]);
    }, [this.operators, defaultOperators]);

    const [operatorOptions, setOperatorOptions] = useState<Option[]>([]);

    useEffect(() => {
      const options = operators.map(o => ({
        title: friendlyMultiSelectOperator(o),
        subtitle: null,
        id: o,
      }));
      setOperatorOptions(options);
    }, [operators]);

    const [filterId, setFilterId] = useState<string | null>(null);
    const [selectedOperator, setSelectedOperator] = useState<Operator | null>(
      null,
    );

    useEffect(() => {
      let filterValue = thisFilter?.filter ?? null;
      if (filterValue && !Array.isArray(filterValue)) {
        filterValue = [filterValue as string];
      }
      setFilterId(filterValue?.[0] ?? null);

      const filterOperator = thisFilter?.operator ?? null;
      setSelectedOperator(filterOperator);
    }, [thisFilter, this.name]);

    useEffect(() => {
      if (selectedOperator && filterId) {
        const filter = {
          operator: selectedOperator,
          filter: [filterId],
        };
        this.filters.changeValue.next({
          name: this.name,
          reduce: () => filter,
        });
      }
    }, [filterId, selectedOperator, this.name]);

    const [showDropdown, setShowDropdown] = useState<boolean>(false);

    useEffect(() => {
      if (selectedOperator !== null) {
        setShowDropdown(Operators.arity(selectedOperator) !== 'NULLARY');
      } else {
        setShowDropdown(false);
      }
    }, [selectedOperator]);

    return {
      operatorOptions,
      selectedOperator,
      showDropdown,
      filterId,
      setFilterId,
      setSelectedOperator,
    };
  }

  readonly lockedHelpText = "You don't have permission to change this item";
}
